import { usePlayer } from "@utils/player/core/usePlayer";
import LBox from "@components/LBox";
import config from "@utils/config";
import { Platform } from "react-native";
import { useSafeAreaFrame } from "react-native-safe-area-context";
import { useStableWindowDimensions } from "@hooks/useStableWindowDimensions";
import FeedAccess from "@components/Exclusives/FeedAccess";
import LGradient from "@components/LGradient";
import { useTopBarHeight } from "@hooks/useTopBarHeight";
import AdView from "@components/Ad/AdView";
import Loading from "@components/Exclusives/Loading";
import { BackgroundColor, theme } from "@theme";
import AccessCarousel from "@components/Exclusives/FeedAccess/AccessCarousel";
import { getViewPort } from "@utils/web/config";
import { useAuth } from "@utils/firebaseAuth/auth";
import { useMe } from "@hooks/trpc/useMe";
import LText from "@components/LText";
import LButton from "@components/LButton";
import { useAuthSheet } from "@components/modals/AuthModal/useAuthSheet";
import { useTabBarHeight } from "@hooks/useTabBarHeight";
import SwipeList from "@components/SwipeList/SwipeList";
import { SwipeListComponentProps } from "@components/SwipeList/types";

import { useEffect } from "react";
import Animated, {
  useAnimatedStyle,
  useSharedValue,
} from "react-native-reanimated";

interface FeedProps {
  isLoading?: boolean;
  loadingBg?: BackgroundColor;
  queueType: "foryou" | "playlist";
}

const NotLoggedIn = () => {
  const window = useStableWindowDimensions();
  const { width: WINDOW_WIDTH, height: WINDOW_HEIGHT } = getViewPort(window);
  const tabBarHeight = useTabBarHeight();
  const openAuthSheet = useAuthSheet();
  return (
    <LBox
      position="absolute"
      top={0}
      left={0}
      right={0}
      bottom={tabBarHeight + config.UI.barMusicPlayerHeight}
      alignItems="center"
      justifyContent="center"
      pointerEvents="none"
    >
      <LBox
        width={WINDOW_WIDTH}
        bg="feedNotLoggedInBg"
        p="m1"
        flexDirection="row"
        position="absolute"
        bottom={
          (WINDOW_HEIGHT - tabBarHeight - config.UI.barMusicPlayerHeight) * 0.25
        }
      >
        <LBox gap="s1">
          <LText variant="h1">Guest Mode</LText>
          <LText variant="bodyM" color="textGrey">
            Sign up to unlock all features
          </LText>
        </LBox>
        <LBox flex={1} alignItems="flex-end" pointerEvents="auto">
          <LButton
            onPress={openAuthSheet}
            sentryEventName="not_logged_in_continue"
            variant="roundedWhite"
            label="TRY FOR FREE"
            width="100%"
            textVariant="bodyMBold"
            py="s3_5"
          />
        </LBox>
      </LBox>
    </LBox>
  );
};

const Feed = ({ isLoading, loadingBg, queueType }: FeedProps) => {
  const window = useStableWindowDimensions();
  const { width: WINDOW_WIDTH, height: WINDOW_HEIGHT } = getViewPort(window);
  const frame = useSafeAreaFrame();
  const _topBarHeight = useTopBarHeight();
  const topBarHeight = _topBarHeight - (WINDOW_HEIGHT / 100) * 3.5;
  const tabBarHeight = useTabBarHeight();
  const { status: authStatus } = useAuth();
  const isLoggedIn =
    authStatus === "logged-in" || authStatus === "firebase-auth-pending";
  const { query: meQuery } = useMe();
  const onboardingStep = meQuery.data?.onboardingStep;
  const onboardingDone = onboardingStep === "completed";

  const isActive = usePlayer((state) => state.activeQueueType === queueType);
  const isActiveShared = useSharedValue(isActive ? 1 : 0);

  useEffect(() => {
    isActiveShared.value = isActive ? 1 : 0;
  }, [isActive, isActiveShared]);

  // Animated style for AccessCarousel visibility - runs on UI thread
  const carouselStyle = useAnimatedStyle(() => ({
    opacity: isActiveShared.value,
    // Move off-screen when inactive to skip rendering
    transform: [{ translateY: isActiveShared.value ? 0 : 1000 }],
  }));

  const topUITotalHeight = topBarHeight;
  const bottomUITotalHeight =
    tabBarHeight + config.UI.barMusicPlayerHeight - theme.spacing.s3;

  // useSafeAreaFrame provides consistent dimensions across Android versions.
  // On Android, use frame.height which represents the full screen including system bars.
  // On iOS, continue using WINDOW_HEIGHT for compatibility.
  const screenHeight = Platform.OS === "android" ? frame.height : WINDOW_HEIGHT;

  const scrollableHeight = screenHeight - bottomUITotalHeight - topBarHeight;

  const renderItem = ({ item }: SwipeListComponentProps) => (
    <FeedAccess data={item} height={scrollableHeight} width={WINDOW_WIDTH} />
  );

  // When active, SwipeList uses root provider (synchronized with BarMusicPlayer)
  // When inactive, wrap with feed-specific provider to maintain scroll position
  const content = (
    <LBox>
      <>
        <>
          <LBox
            height={Platform.OS === "android" ? "100%" : screenHeight}
            top={0}
            left={0}
            right={0}
            style={{ paddingTop: topBarHeight }}
          >
            {isLoading && (
              <LBox
                bottom={0}
                left={0}
                position="absolute"
                right={0}
                top={0}
                zIndex={1}
                backgroundColor={loadingBg}
              >
                <Loading />
              </LBox>
            )}
            {/* <LBox opacity={isLoading ? 0 : 1}> */}
            <SwipeList
              Component={renderItem}
              disabled={isLoading}
              height={scrollableHeight}
              queueType={queueType}
              surface="feed"
            />
            {/* </LBox> */}
            <LBox
              backgroundColor="black"
              position="absolute"
              top={0}
              height={topUITotalHeight}
              width={WINDOW_WIDTH}
            />
            <LGradient
              colors={["black", "transparent"]}
              locations={[0, 0.4]}
              position="absolute"
              top={topUITotalHeight}
              height={50}
              width={WINDOW_WIDTH}
            />
            {/* AccessCarousel always mounted, visibility controlled by UI thread */}
            {onboardingDone && (
              <Animated.View
                style={[
                  {
                    position: "absolute",
                    alignSelf: "center",
                    bottom: bottomUITotalHeight + theme.spacing.l1,
                  },
                  carouselStyle,
                ]}
                pointerEvents={isActive ? "auto" : "none"}
              >
                <>
                  <AccessCarousel queueType={queueType} />
                </>
              </Animated.View>
            )}
            <LBox
              backgroundColor="black"
              position="absolute"
              bottom={0}
              height={bottomUITotalHeight}
              width={WINDOW_WIDTH}
            />
          </LBox>
          {!isLoggedIn && <NotLoggedIn />}
          <AdView topPadding={topBarHeight} />
        </>
      </>
    </LBox>
  );

  return content;
};

export default Feed;
